

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { blogData } from "../Blog/BlogData";
import { ArrowUpRight, Plus, Minus } from "lucide-react";

const Blogs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  const sortedBlogs = [...blogData].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = sortedBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(sortedBlogs.length / blogsPerPage);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  return (
    <section className="bg-[#0A0A0A] text-white pt-48 pb-32 px-6 lg:px-20 min-h-screen">
      
      {/* --- BACKGROUND ACCENT --- */}
      <div className="absolute top-0 right-0 w-1/2 h-screen bg-[#B68D40]/5 blur-[120px] -z-10 pointer-events-none" />

      <div className="max-w-screen-2xl mx-auto">
        
        {/* TOP HEADER: ASYMMETRIC */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-32 border-b border-white/10 pb-16">
          <div className="max-w-2xl">
            <h2 className="text-7xl md:text-9xl font-black tracking-tighter leading-[0.8] mb-8 uppercase">
              The <br /> <span className="text-[#B68D40]">Journal</span>
            </h2>
            <p className="text-gray-500 font-mono text-sm tracking-widest uppercase">
              // Legal Intelligence & Global Jurisprudence
            </p>
          </div>
          <div className="mt-10 md:mt-0 text-right">
            <p className="text-gray-400 max-w-xs ml-auto text-sm leading-relaxed italic">
              "In the hall of justice, the only real weapon is knowledge. We share ours freely with those who seek it."
            </p>
          </div>
        </div>

        {/* BLOG LIST: MODERN MINIMALIST LIST */}
        <div className="space-y-0">
          {currentBlogs.map((blog, index) => (
            <Link
              to={`/blogs/${blog.id}`}
              key={blog.id}
              className="group relative block border-b border-white/10 py-16 transition-all duration-700 hover:bg-white/[0.02]"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-12 relative z-10">
                
                {/* INDEX */}
                <div className="hidden lg:block text-[#B68D40] font-mono text-xl opacity-40 group-hover:opacity-100 transition-opacity">
                  0{indexOfFirstBlog + index + 1}
                </div>

                {/* MAIN CONTENT */}
                <div className="flex-grow max-w-3xl">
                  <div className="flex items-center gap-4 mb-4 text-[10px] font-bold uppercase tracking-[0.4em] text-gray-500">
                    <span>{blog.category}</span>
                    <span className="w-8 h-px bg-white/20 group-hover:bg-[#B68D40] transition-all"></span>
                    <span>{new Date(blog.date).getFullYear()}</span>
                  </div>
                  <h3 className="text-4xl md:text-6xl font-light tracking-tight group-hover:italic transition-all duration-500">
                    {blog.title}
                  </h3>
                </div>

                {/* HOVER REVEAL IMAGE (DESKTOP ONLY) */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-64 h-80 opacity-0 group-hover:opacity-100 group-hover:translate-x-20 transition-all duration-700 pointer-events-none rotate-6 group-hover:rotate-0">
                    <img 
                      src={blog.image} 
                      className="w-full h-full object-cover shadow-2xl grayscale hover:grayscale-0 transition-all duration-1000" 
                      alt=""
                    />
                </div>

                {/* MOBILE/TABLET IMAGE (VISIBLE ONLY ON SMALLER) */}
                <div className="lg:hidden w-full aspect-video overflow-hidden">
                   <img src={blog.image} className="w-full h-full object-cover opacity-50" alt="" />
                </div>

                {/* ARROW ICON */}
                <div className="ml-auto">
                    <div className="w-16 h-16 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-[#B68D40] group-hover:border-[#B68D40] group-hover:text-black transition-all duration-500 transform group-hover:rotate-45">
                        <ArrowUpRight size={24} />
                    </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* PAGINATION: BRUTALIST STYLE */}
        {totalPages > 1 && (
          <div className="mt-40 flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="text-[10px] uppercase tracking-[0.5em] text-gray-500 font-bold">
              Showing Page {currentPage} of {totalPages}
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-12 h-12 flex items-center justify-center border border-white/10 hover:bg-white hover:text-black disabled:opacity-20 transition-all"
              >
                <Minus size={16} />
              </button>

              <div className="flex gap-2">
                 {[...Array(totalPages)].map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentPage(index + 1)}
                        className={`w-12 h-12 flex items-center justify-center border border-white/10 transition-all ${
                            currentPage === index + 1 ? "bg-[#B68D40] text-black border-[#B68D40]" : "hover:bg-white/5"
                        }`}
                    >
                        {index + 1}
                    </button>
                 ))}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-12 h-12 flex items-center justify-center border border-white/10 hover:bg-white hover:text-black disabled:opacity-20 transition-all"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default Blogs;